import { useMemo } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";
import { buildChartData, hideSeries, sortChartData, type ChartCustomization, type ChartSpec } from "@brasilnovo/shared";
import { Table, brl, num } from "./ui.js";
import { humanizeField } from "../lib/labels.js";

// Paleta liderada pelo bicolor da marca Brasil Novo (verde · dourado),
// distinguível (inclui daltonismo comum) + cores semânticas de status reusadas
// do portal. Ver docs/identidade-visual.md e CHARTS_STUDY §7.
const PALETTE = ["#117849", "#e5aa36", "#1f9d68", "#7c3aed", "#ca8821", "#db2777", "#0d9488", "#64748b"];
const STATUS_COLORS: Record<string, string> = {
  LIBERADO: "#117849", AGENDADO: "#e5aa36", CARREGADO: "#1f9d68",
  Atraso: "#dc2626", PBTC: "#dc2626", OK: "#16a34a", OUTROS: "#94a3b8", Outros: "#94a3b8",
};
const colorFor = (key: string, i: number) => STATUS_COLORS[key] ?? PALETTE[i % PALETTE.length];

const CURRENCY = /(frete|valor|tarifa|receita|faturamento)/i;
const TEMPORAL = /(competencia|compet[êe]ncia|data|m[êe]s|periodo|per[íi]odo)/i;

const compactNum = (v: number) =>
  Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : num(v);

export function ChartRenderer({
  spec, rows, columns, height = 320, custom,
}: {
  spec: ChartSpec;
  rows: Record<string, unknown>[];
  columns: string[];
  height?: number;
  custom?: ChartCustomization;
}) {
  const data = useMemo(() => {
    let d = buildChartData(spec, rows);
    d = sortChartData(d, custom?.sort);
    d = hideSeries(d, custom?.hiddenSeries);
    return d;
  }, [spec, rows, custom]);
  const { seriesKeys, xKey } = data;

  // Domínio do eixo de valor (reposicionar limites). "auto" = automático.
  const vMin = custom?.yMin ?? "auto";
  const vMax = custom?.yMax ?? "auto";
  const valueDomain: [number | string, number | string] = [vMin, vMax];

  // Moeda quando a medida/serie é financeira; formata eixo e tooltip.
  const measureName = spec.measure ?? spec.series[0] ?? seriesKeys[0] ?? "";
  const isCurrency = CURRENCY.test(measureName) || (!spec.groupBy && seriesKeys.some((k) => CURRENCY.test(k)));
  const fmt = (v: number) => (isCurrency ? brl(v) : num(v));
  const axisFmt = (v: number) => (isCurrency ? compactNum(v) : compactNum(v));
  const isTemporalX = !!xKey && TEMPORAL.test(xKey);
  const xTick = (v: unknown) => (isTemporalX ? String(v).slice(0, 7) : String(v));

  const legend = seriesKeys.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null;
  const tooltip = (
    <Tooltip
      formatter={(v: number, name: string) => [fmt(v), name]}
      labelFormatter={(l) => xTick(l)}
      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
    />
  );

  // ---- TABELA ----
  if (spec.kind === "table") {
    return (
      <Table
        columns={(columns.length ? columns : Object.keys(rows[0] ?? {})).map((c) => ({
          key: c, label: humanizeField(c),
          render: CURRENCY.test(c) ? (v: unknown) => brl(v as number) : undefined,
        }))}
        rows={rows.slice(0, 200)}
      />
    );
  }

  // ---- KPI ----
  if (spec.kind === "kpi") {
    const r0 = rows[0] ?? {};
    return (
      <div className="flex flex-wrap gap-6 py-4">
        {seriesKeys.map((k) => (
          <div key={k} role="group" aria-label={humanizeField(k)}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{humanizeField(k)}</div>
            <div className="mt-1 text-3xl font-bold text-slate-900">
              {CURRENCY.test(k) ? brl(Number(r0[k])) : num(Number(r0[k]))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- PIZZA ----
  if (spec.kind === "pie") {
    return (
      <div>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie data={data.rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={Math.min(height / 2 - 20, 130)} label={(e: { name: string }) => (e.name.length > 16 ? `${e.name.slice(0, 15)}…` : e.name)}>
              {data.rows.map((r, i) => {
                const name = String((r as { name: string }).name);
                // key estável pelo nome (a fatia é única por categoria) — evita
                // troca de cor na reconciliação ao reordenar/trocar dados.
                return <Cell key={`pie-${name}`} fill={colorFor(name, i)} />;
              })}
            </Pie>
            <Tooltip formatter={(v: number, _n, p) => [fmt(v), String((p?.payload as { name?: string })?.name ?? "")]} />
          </PieChart>
        </ResponsiveContainer>
        <TruncNote data={data} />
      </div>
    );
  }

  // ---- DISPERSÃO ----
  if (spec.kind === "scatter") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis type="number" dataKey="x" name={spec.x} fontSize={11} tickFormatter={axisFmt} />
          <YAxis type="number" dataKey="y" name={spec.y} fontSize={11} tickFormatter={axisFmt} />
          <ZAxis range={[60, 60]} />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v: number) => num(v)} />
          <Scatter data={data.rows} fill="#117849" />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  // ---- RANKING HORIZONTAL ----
  if (spec.kind === "horizontalBar") {
    const k = seriesKeys[0];
    return (
      <div>
        <ResponsiveContainer width="100%" height={Math.max(height, data.rows.length * 26 + 40)}>
          <BarChart data={data.rows} layout="vertical" margin={{ left: 8, right: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
            <XAxis type="number" fontSize={11} tickFormatter={axisFmt} domain={valueDomain} allowDataOverflow />
            <YAxis type="category" dataKey={xKey} width={130} fontSize={11} tick={{ fill: "#475569" }} />
            {tooltip}
            <Bar dataKey={k} fill="#117849" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <TruncNote data={data} />
      </div>
    );
  }

  // ---- LINHA / ÁREA ----
  if (spec.kind === "line" || spec.kind === "area") {
    const Chart = spec.kind === "line" ? LineChart : AreaChart;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <Chart data={data.rows} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey={xKey} tickFormatter={xTick} fontSize={11} />
          <YAxis fontSize={11} tickFormatter={axisFmt} domain={valueDomain} allowDataOverflow />
          {tooltip}
          {legend}
          {seriesKeys.map((k, i) =>
            spec.kind === "line" ? (
              <Line key={k} type="monotone" dataKey={k} stroke={colorFor(k, i)} strokeWidth={2} dot={false} name={k} />
            ) : (
              <Area key={k} type="monotone" dataKey={k} stackId={spec.groupBy ? "s" : undefined} stroke={colorFor(k, i)} fill={colorFor(k, i)} fillOpacity={0.25} name={k} />
            ),
          )}
        </Chart>
      </ResponsiveContainer>
    );
  }

  // ---- BARRAS (bar / stackedBar / groupedBar) ----
  const stacked = spec.kind === "stackedBar";
  // Muitos rótulos no eixo X → inclina e reserva altura para não sobrepor.
  const manyX = data.rows.length > 8;
  return (
    <div>
      <ResponsiveContainer width="100%" height={manyX ? height + 36 : height}>
        <BarChart data={data.rows} margin={{ top: 8, right: 16, left: 8, bottom: manyX ? 40 : 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis
            dataKey={xKey}
            tickFormatter={xTick}
            fontSize={11}
            interval={manyX ? 0 : "preserveStartEnd"}
            angle={manyX ? -35 : 0}
            textAnchor={manyX ? "end" : "middle"}
            height={manyX ? 60 : 30}
          />
          <YAxis fontSize={11} tickFormatter={axisFmt} domain={valueDomain} allowDataOverflow />
          {tooltip}
          {legend}
          {seriesKeys.map((k, i) => (
            <Bar key={k} dataKey={k} stackId={stacked ? "s" : undefined} fill={colorFor(k, i)} radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]} name={k} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <TruncNote data={data} />
    </div>
  );
}

// Aviso honesto quando o gráfico mostra só os top-N (sem mascarar truncamento).
function TruncNote({ data }: { data: ReturnType<typeof buildChartData> }) {
  if (!data.truncated) return null;
  return (
    <div className="mt-1 flex items-center justify-center gap-1 text-xs font-medium text-amber-700">
      <span aria-hidden>⚠</span> mostrando os {data.truncated.shown} maiores de {data.truncated.total}
    </div>
  );
}
